import React from 'react';
import { Check, Minus } from 'lucide-react';
import { COLUMN_BY_ID, formatStamp, formatBool, formatStatus } from '../columns';
import { DELIMITERS } from '../csv';

// A representative value for each preview, so the panel shows what the file
// will actually contain rather than the name of a setting.
const SAMPLE_STAMP = '2026-09-15T09:24:13.337';
const SAMPLE_STATUS_ROW = { status: 'V', statusLabel: 'Voided' };

const Row = ({ label, children }) => (
  <div className="flex gap-3 py-1">
    <span className="text-slate-500 w-24 flex-shrink-0">{label}</span>
    <span className="text-slate-200 flex-1 min-w-0">{children}</span>
  </div>
);

const Flag = ({ on, children }) => (
  <span className={`inline-flex items-center gap-1 ${on ? 'text-slate-300' : 'text-slate-600'}`}>
    {on ? <Check className="w-3 h-3 text-green-500" /> : <Minus className="w-3 h-3" />}
    {children}
  </span>
);

/**
 * What "Quick export" will produce right now, resolved from the user's saved
 * settings. Everything here is derived the same way csv.js derives it, so the
 * panel cannot drift from the file.
 */
const QuickExportSummary = ({ preset, scope, filteredCount, allCount, filterSummary, filename }) => {
  const { selection, options } = preset;
  const rowCount = scope === 'all' ? allCount : filteredCount;
  const delimiter = DELIMITERS.find((d) => d.id === options.delimiter) || DELIMITERS[0];

  return (
    <div className="w-[26rem] max-w-[calc(100vw-2rem)] bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-4 text-xs">
      <p className="text-slate-300 font-medium mb-2">Quick export will produce</p>

      <div className="divide-y divide-slate-800">
        <Row label="Rows">
          <span className="text-slate-100 font-medium">{rowCount.toLocaleString()}</span>{' '}
          {scope === 'all'
            ? <span className="text-slate-400">— everything in the date range</span>
            : <span className="text-slate-400">— the filtered rows on screen</span>}
          {scope === 'filtered' && (
            <div className="text-slate-500 mt-0.5">Filters: {filterSummary}</div>
          )}
        </Row>

        <Row label={`Columns (${selection.length})`}>
          {selection.length === 0 ? (
            <span className="text-amber-400">None selected</span>
          ) : (
            <span className="text-slate-300">
              {selection.map((c, i) => {
                const renamed = c.header !== COLUMN_BY_ID[c.id]?.label;
                return (
                  <React.Fragment key={c.id}>
                    {i > 0 && <span className="text-slate-600">, </span>}
                    <span
                      className={renamed ? 'text-blue-300' : ''}
                      title={renamed ? `Renamed from "${COLUMN_BY_ID[c.id]?.label}"` : undefined}
                    >
                      {c.header}
                    </span>
                  </React.Fragment>
                );
              })}
            </span>
          )}
          <div className="text-slate-500 mt-0.5">
            {selection.length
              ? 'In this order, left to right.'
              : 'Pick columns in Export CSV… before using Quick export.'}
          </div>
        </Row>

        <Row label="Dates look like">
          <span className="font-mono text-slate-300">{formatStamp(SAMPLE_STAMP, options.dateFormat)}</span>
        </Row>

        <Row label="Status as">
          <span className="font-mono text-slate-300">{formatStatus(SAMPLE_STATUS_ROW, options.statusFormat)}</span>
        </Row>

        <Row label="Yes / no as">
          <span className="font-mono text-slate-300">
            {formatBool(true, options.boolFormat)} / {formatBool(false, options.boolFormat)}
          </span>
        </Row>

        <Row label="Separator">
          {delimiter.short}
          {options.quoteMode === 'always' && <span className="text-slate-500">, every field quoted</span>}
        </Row>

        <Row label="Empty cells">
          {options.nullText
            ? <span className="font-mono text-slate-300">{options.nullText}</span>
            : <span className="text-slate-400">left blank</span>}
        </Row>

        <Row label="For Excel">
          <span className="flex flex-wrap gap-x-3 gap-y-0.5">
            <Flag on={options.includeHeader}>header row</Flag>
            <Flag on={options.includeBom}>UTF-8 BOM</Flag>
            <Flag on={options.forceTextTracking}>tracking as text</Flag>
            <Flag on={options.lineEnding === 'crlf'}>CRLF</Flag>
          </span>
        </Row>

        <Row label="File name">
          <span className="font-mono text-slate-300 break-all">{filename}</span>
        </Row>
      </div>

      <p className="text-slate-500 mt-3 pt-2 border-t border-slate-800">
        Change any of this in <span className="text-slate-400">Export CSV…</span> — downloading there
        saves it back here.
      </p>
    </div>
  );
};

export default QuickExportSummary;
